import Tesseract from 'tesseract.js';

// Gujarati number to Arabic mapping
const GUJARATI_DIGITS = {
  '૦': '0', '૧': '1', '૨': '2', '૩': '3', '૪': '4',
  '૫': '5', '૬': '6', '૭': '7', '૮': '8', '૯': '9'
};

// Convert Gujarati numbers to Arabic
const gujaratiToArabic = (text) => {
  return text.split('').map(char => GUJARATI_DIGITS[char] || char).join('');
};

// Extract amount from text
const extractAmount = (text) => {
  if (!text) return null;
  
  // Clean text
  let cleaned = text.replace(/[,\s]/g, '');
  cleaned = gujaratiToArabic(cleaned);
  
  // Try different patterns
  const patterns = [
    /₹(\d+)/,
    /Rs\.?\s*(\d+)/,
    /(\d+)\s*રૂ/,
    /(\d+)\s*rupees?/i,
    /(\d+)/
  ];
  
  for (const pattern of patterns) {
    const match = cleaned.match(pattern);
    if (match) {
      const amount = parseInt(match[1], 10);
      if (amount > 0 && amount < 10000000) {
        return amount;
      }
    }
  }
  
  return null;
};

// Clean name text
const cleanName = (text) => {
  if (!text) return '';
  
  let cleaned = text
    .replace(/[0-9]/g, '')
    .replace(/[^\w\s\u0A80-\u0AFF]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  
  return cleaned;
};

// Clean place text
const cleanPlace = (text) => {
  if (!text) return '';
  
  let cleaned = text
    .replace(/[^\w\s\u0A80-\u0AFF.,-]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  
  return cleaned;
};

// Check if row is likely a header
const isHeaderRow = (text) => {
  const headerKeywords = ['ક્રમ', 'નામ', 'સ્થળ', 'રકમ', 'Name', 'Place', 'Amount', 'Serial'];
  return headerKeywords.some(keyword => text.includes(keyword));
};

// Check if row is empty or decorative
const isEmptyRow = (text) => {
  const cleaned = text.replace(/[\s\-\|=]/g, '');
  return cleaned.length < 3;
};

// Parse OCR text into structured entries
const parseOCRText = (text, pageNumber) => {
  const entries = [];
  const lines = text.split('\n').filter(line => line.trim());
  
  for (const line of lines) {
    const trimmed = line.trim();
    
    // Skip headers, empty rows, decorative elements
    if (isEmptyRow(trimmed) || isHeaderRow(trimmed)) {
      continue;
    }
    
    // Skip lines with only symbols or short text
    if (trimmed.length < 5) {
      continue;
    }
    
    // Try to split by common delimiters
    const parts = trimmed.split(/[\t|,;]+/).map(p => p.trim()).filter(p => p);
    
    if (parts.length >= 2) {
      // Try to identify name, place, amount
      let name = '';
      let place = '';
      let amount = null;
      
      // Look for amount in any part
      for (let i = 0; i < parts.length; i++) {
        const extractedAmount = extractAmount(parts[i]);
        if (extractedAmount) {
          amount = extractedAmount;
          parts.splice(i, 1);
          break;
        }
      }
      
      // Remaining parts - first is likely name, second is place
      if (parts.length >= 2) {
        name = cleanName(parts[0]);
        place = cleanPlace(parts[1]);
      } else if (parts.length === 1) {
        name = cleanName(parts[0]);
      }
      
      // Add entry if we have meaningful data
      if (name || place || amount) {
        entries.push({
          name: name || null,
          place: place || null,
          amount: amount,
          page: pageNumber
        });
      }
    } else if (parts.length === 1) {
      // Single column - might just be a name
      const name = cleanName(parts[0]);
      if (name && name.length > 2) {
        entries.push({
          name: name,
          place: null,
          amount: null,
          page: pageNumber
        });
      }
    }
  }
  
  return entries;
};

// Preprocess image for better OCR
const preprocessImage = async (imageFile) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Resize if too large
      let { width, height } = img;
      const maxSize = 2000;
      
      if (width > maxSize || height > maxSize) {
        const scale = maxSize / Math.max(width, height);
        width *= scale;
        height *= scale;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw image
      ctx.drawImage(img, 0, 0, width, height);
      
      // Get image data for processing
      const imageData = ctx.getImageData(0, 0, width, height);
      const data = imageData.data;
      
      // Convert to grayscale and increase contrast
      for (let i = 0; i < data.length; i += 4) {
        const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
        
        // Apply threshold for better OCR
        const threshold = 128;
        const value = gray > threshold ? 255 : 0;
        
        data[i] = value;
        data[i + 1] = value;
        data[i + 2] = value;
      }
      
      ctx.putImageData(imageData, 0, 0);
      
      canvas.toBlob(resolve, 'image/png');
    };
    
    img.src = URL.createObjectURL(imageFile);
  });
};

// Main extraction function
export const extractGuestRecords = async (imageData, pageNumber, onProgress) => {
  const { file, name } = imageData;
  
  // Preprocess image
  onProgress(0.1);
  
  let processedImage;
  try {
    processedImage = await preprocessImage(file);
  } catch (error) {
    processedImage = file;
  }
  
  // Run OCR with Tesseract.js
  onProgress(0.2);
  
  const result = await Tesseract.recognize(
    processedImage,
    'guj+eng',
    {
      logger: (m) => {
        if (m.status === 'recognizing text') {
          onProgress(0.2 + m.progress * 0.7);
        }
      }
    }
  );
  
  onProgress(0.9);
  
  // Parse the OCR text
  const ocrText = result.data.text;
  const entries = parseOCRText(ocrText, pageNumber);
  
  onProgress(1.0);
  
  return {
    page: pageNumber,
    entries: entries,
    rawText: ocrText,
    confidence: result.data.confidence
  };
};